import type { CapacityPool, CapacityType } from 'features/common/hooks/useEventCapacity';

/**
 * Send-side view of a shared-capacity pool: how many more tickets it can still take.
 *
 * `remaining` is the Lifetime-Σ budget — `total_amount − Σ member issued` (issued = sold + comped, since a
 * comped seat is still a seat taken) — matching the pool analytics (docs/pools_business_logic.md §3), NOT the
 * wobbly `pool.sold` scalar. It is the dual cap the backend enforces (`ReserveTicketsRedis`) surfaced
 * client-side, so a manager can't fill two pooled tiers past their shared capacity and only learn on submit.
 */
export interface SendPool {
	poolId: number;
	name: string;
	totalAmount: number;
	/** Σ member issued (sold + comped). */
	issued: number;
	/** total_amount − issued, clamped ≥ 0 — the pool budget used for the per-tier clamp + on-send gate. */
	remaining: number;
	/**
	 * The shared remaining the pool's ON-SALE tiers can ACTUALLY draw = min(remaining, Σ on-sale member own
	 * remaining). This is the honest "available to send across this pool" headline the tile shows — it never
	 * overstates an oversized pool whose members' own quantity is the real ceiling. The per-tier steppers'
	 * own-remaining bound makes the requestable sum equal this anyway, so `remaining` stays the clamp basis.
	 */
	sellableRemaining: number;
	/** Σ member issued > total_amount — a legacy over-subscribed pool. Drives the tile's warning state. */
	overCapacity: boolean;
	memberNames: string[];
}

export interface SendPoolBudget {
	hasPools: boolean;
	pools: SendPool[];
	/** ticket-type NAME → its pool id — the stable join key (IDs differ across event / public endpoints). */
	poolIdByTypeName: Record<string, number>;
	/** public ticket-type id → its pool id — for bulk rows keyed by `ticket_type_id`. */
	poolIdByTypeId: Record<number, number>;
	/** pool id → remaining sendable budget. */
	remainingByPoolId: Record<number, number>;
}

/** Derives the send-side pool budget from the shared `useEventCapacity` source (single source of truth). */
export function buildSendPoolBudget(pools: CapacityPool[], types: CapacityType[]): SendPoolBudget {
	// The type→pool maps are the only thing derived from `types` here — they resolve a requested tier (by
	// name in the single form, by id in bulk) to its pool for the clamp + on-send gate.
	const poolIdByTypeName: Record<string, number> = {};
	const poolIdByTypeId: Record<number, number> = {};

	for (const tt of types) {
		if (tt.poolId == null) continue;
		poolIdByTypeName[tt.name] = tt.poolId;
		poolIdByTypeId[tt.typeId] = tt.poolId;
	}

	const remainingByPoolId: Record<number, number> = {};
	const sendPools: SendPool[] = pools.map((p) => {
		// Read the hook's Lifetime-Σ values DIRECTLY (`p.sold` = Σ member issued, `p.poolRemaining` =
		// total − sold) — the SAME numbers the Show-Dashboard tile and the analytics pool card show, so the
		// three surfaces reconcile by construction (used + available = total). Re-summing `tt.sold` here used
		// to diverge from the hook's snapshot-resolved issued and produced the wrong X/Y on this page.
		remainingByPoolId[p.poolId] = p.poolRemaining;
		return {
			poolId: p.poolId,
			name: p.name,
			totalAmount: p.totalAmount,
			issued: p.sold,
			remaining: p.poolRemaining,
			sellableRemaining: p.sellableRemaining,
			overCapacity: p.sold > p.totalAmount,
			memberNames: p.memberNames,
		};
	});

	return {
		hasPools: sendPools.length > 0,
		pools: sendPools,
		poolIdByTypeName,
		poolIdByTypeId,
		remainingByPoolId,
	};
}

/**
 * Sums requested quantities into a `pool id → total` map. Each item carries its resolved `poolId`
 * (via {@link SendPoolBudget.poolIdByTypeName} for the single form, `poolIdByTypeId` for bulk rows)
 * and a `quantity`; items with no pool (`poolId === undefined`) are ignored.
 */
export function sumRequestedByPool(items: { poolId: number | undefined; quantity: number }[]): Record<number, number> {
	const map: Record<number, number> = {};
	for (const { poolId, quantity } of items) {
		if (poolId === undefined) continue;
		map[poolId] = (map[poolId] ?? 0) + (quantity || 0);
	}
	return map;
}

/** A requested quantity tagged with its resolved pool id (undefined when the tier is not pooled). */
export interface PoolRequestItem {
	poolId: number | undefined;
	quantity: number;
}

/**
 * Maps bulk editable rows to {@link PoolRequestItem}s (id join — rows carry the public ticket-type id).
 * The single source for both the bulk on-send gate and the live bulk red-ring, so they can never disagree.
 */
export function poolItemsFromBulkRows(
	rows: { ticketTypeId: number | null; quantity: number }[],
	budget: SendPoolBudget
): PoolRequestItem[] {
	return rows.map((row) => ({
		poolId: row.ticketTypeId !== null ? budget.poolIdByTypeId[row.ticketTypeId] : undefined,
		quantity: row.quantity,
	}));
}

/**
 * Maps single-form recipients to {@link PoolRequestItem}s (name join — the stable key across endpoints).
 * The single source for both the single-recipient on-send gate and its live red-ring.
 */
export function poolItemsFromRecipients(
	recipients: { ticketTypes: { type: string; quantity: number }[] }[],
	budget: SendPoolBudget
): PoolRequestItem[] {
	return recipients.flatMap((r) =>
		r.ticketTypes.map((tt) => ({ poolId: budget.poolIdByTypeName[tt.type], quantity: tt.quantity }))
	);
}

/**
 * Pools whose total requested quantity (summed across their member tiers) exceeds the pool's **sellable**
 * remaining — the honest send ceiling the tile shows (`min(poolRemaining, Σ on-sale member own-remaining)`),
 * NOT raw `poolRemaining`. For a normal pool the two are equal; for an oversized pool (member own-remaining is
 * the tighter bound) `sellableRemaining < poolRemaining`, so this fires exactly when a request exceeds what the
 * tiers can actually draw — matching the displayed available and the per-tier stepper clamp. Drives the live
 * over-reach ring (pool tile + bulk stepper) and the lowest-priority on-send gate.
 */
export function getExceededPools(requestedByPoolId: Record<number, number>, budget: SendPoolBudget): SendPool[] {
	return budget.pools.filter((p) => (requestedByPoolId[p.poolId] ?? 0) > p.sellableRemaining);
}

/**
 * The live send ceiling for one pooled tier: `min(tier own-remaining, poolRemaining − what every OTHER
 * same-pool request already draws)`. `requests[selfIndex]` is the request being capped; `poolId` is the
 * tier's resolved pool (`undefined` for a non-pooled tier or when there is no budget → its own remaining is
 * returned unchanged). The single clamp BOTH Send modes call — the single-recipient steppers
 * (`PunchupQuantityCard`, requests tagged by name) and the bulk rows (`BulkUploadTable`, tagged by id) — so a
 * manager cannot over-send a pool in either mode and the two can't diverge.
 */
export function poolClampedMax(
	tierOwnRemaining: number,
	poolId: number | undefined,
	requests: PoolRequestItem[],
	selfIndex: number,
	budget: SendPoolBudget | undefined
): number {
	if (budget === undefined || poolId === undefined) return tierOwnRemaining;
	const poolRemaining = budget.remainingByPoolId[poolId] ?? 0;
	const otherDraw = requests.reduce(
		(sum, req, i) => (i !== selfIndex && req.poolId === poolId ? sum + (req.quantity || 0) : sum),
		0
	);
	return Math.min(tierOwnRemaining, Math.max(0, poolRemaining - otherDraw));
}
